import { useEntitiesByTypeQuery } from "@codegen/index";
import { EntityTypes } from "@codegen/schema";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { cn } from "@utils/cn";
import { Command } from "cmdk";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import Spinner from "./spinner";

interface EntityNode {
  id: string;
  type: EntityTypes;
  name?: string;
  companyByCompanyId?: {
    dbaName?: string;
  };
  userByUserId?: {
    fullName?: string;
  };
}

const EntityObjectSelector = (props: {
  type: EntityTypes;
  onSelect: (entity: { id: string; name: string; type: string }) => void;
  selectedId: string;
  refreshed?: boolean;
  resetRefreshed?: () => void;
  entities?: EntityNode[];
}) => {
  const [value, setValue] = useState({
    id: props.selectedId,
    name: "",
    type: props.type,
  });

  const { data, loading, refetch } = useEntitiesByTypeQuery({
    variables: {
      type: props.type,
    },
  });

  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef(null);

  const [open, setOpen] = useState(false);
  const { refreshed, resetRefreshed } = props;
  const options = useMemo(() => {
    if (data == null) return [];
    if (data.allAdditionalEntities?.nodes == null) return [];

    const res = [];
    const entities = props.entities ?? data.allAdditionalEntities.nodes;
    for (const x of entities) {
      if (x == null) continue;

      const name =
        x.type === EntityTypes.Company
          ? x.companyByCompanyId?.dbaName
          : x.type === EntityTypes.User
          ? x.userByUserId?.fullName
          : x.name;

      res.push({
        label: {
          name: name ?? "",
          type: x.type,
          id: x.id as string,
        },
        value: x.id,
      });
    }
    return res;
  }, [data, props.type]);

  useEffect(() => {
    const fetch = async () => {
      await refetch();
      resetRefreshed?.();
    };
    if (refreshed) {
      fetch();
    }
  }, [refreshed, resetRefreshed]);

  useEffect(() => {
    if (props.selectedId == null) return;
    const v = options.find((o) => o.value === props.selectedId);
    if (v == null) return;
    setValue({
      id: v.label.id,
      name: v.label.name,
      type: v.label.type,
    });
  }, [options, props.selectedId, props.type]);

  return (
    <>
      {loading && <Spinner data-testid="entity-selector-loading" />}
      {data && options.length > 0 && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-label="Select entity"
              data-testid={`entity-selector-trigger-${props.type}`}
              className={cn("w-[220px] justify-between bg-white")}
            >
              <p
                className="truncate"
                data-testid={`entity-selector-selected-value-${props.type}`}
              >
                {value.name}
              </p>
              <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="flex flex-col w-[220px] p-2 gap-y-3 overflow-scroll max-h-[200px] bg-background-highlight"
            data-testid="entity-selector-content"
          >
            <Command
              className="w-full flex flex-col gap-y-1"
              data-testid="entity-selector-command"
              filter={(v, search) => {
                const label = options.find((o) => o.value === v)?.label;
                if (label == null) return 0;

                if (label.name.toLowerCase().includes(search.toLowerCase())) {
                  return 1;
                }

                if (label.type.toLowerCase().includes(search.toLowerCase())) {
                  return 0.5;
                }

                if (v.toLowerCase().includes(search.toLowerCase())) {
                  return 0.5;
                }

                return 0;
              }}
            >
              <Command.Input
                ref={inputRef}
                autoFocus
                placeholder="Search for an entity..."
                data-testid={`entity-selector-search-input-${props.type}`}
              />

              <Command.List
                ref={listRef}
                data-testid={`entity-selector-list-${props.type}`}
              >
                <Command.Empty
                  data-testid={`entity-selector-no-results-${props.type}`}
                >
                  No results found.
                </Command.Empty>
                {options.map((v, idx) => (
                  <Command.Item
                    key={idx}
                    value={v.value}
                    onSelect={(currentValue) => {
                      const v = options.find((o) => o.value === currentValue);
                      if (v == null) return;

                      props.onSelect({
                        id: v.label.id,
                        name: v.label.name,
                        type: v.label.type,
                      });
                      setOpen(false);
                    }}
                    className="hover:bg-gray-100 hover:cursor-pointer"
                    data-testid={`entity-selector-option-${v.label.type.toLowerCase()}-${v.label.name
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    <div className="text-sm w-full flex flex-row items-center justify-between gap-x-2">
                      <div
                        className="text-gray-400 font-semibold"
                        data-testid={`entity-selector-name-${v.label.type.toLowerCase()}-${v.label.name
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                      >
                        {v.label.name}
                      </div>
                      <div
                        className="text-xs text-gray-400 font-semibold"
                        data-testid={`entity-selector-type-${v.label.type.toLowerCase()}-${v.label.name
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                      >
                        {v.label.type}
                      </div>
                    </div>
                  </Command.Item>
                ))}
              </Command.List>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
};

export default EntityObjectSelector;
